import {
    AlertColor,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import Block from '../../components/Block';
import Column from '../../components/Column';
import Heading from '../../components/Heading';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Grid from '@mui/system/Grid/Grid';
import { Send, Visibility } from '@mui/icons-material';
import ApiService from '../../ApiService';
import { InfoMessage, InfoMessageProps } from '../../components/InfoMessage';
import { EmailTemplateDTO } from '../../types/EmailTest';
import usePageTitle from '../../hooks/usePageTitle';

// ---------------------------------------------------------------------------
// Default sample values for template variables
// ---------------------------------------------------------------------------
const VARIABLE_DEFAULTS: Record<string, string> = {
    advertiserName: 'Test Advertiser',
    agencyName: 'Test Agency',
    campaignName: 'Test Campaign',
    dealerName: 'Test Advertiser',
    submittedBy: 'Test User',
    createdBy: 'Test User',
    modifiedBy: 'Test User',
    approvedBy: 'Test User',
    userName: 'Test User',
    username: 'testuser@example.com',
    password: 'TempPass123!',
    mediaType: 'Display',
    campaignType: 'Standard',
    strategyType: 'Awareness',
    startDate: '2025-01-01',
    endDate: '2025-12-31',
    beginDate: '2025-01-01',
    budget: '5000',
    emailForApproval: 'approver@example.com',
    inventoryProvider: 'Amazon',
    additionalComments: 'Test comment',
    changeRequestStartDate: '2025-02-01',
    changeRequestEndDate: '2025-12-31',
    changeRequestBudget: '7500',
    changeRequestComments: 'Budget increase requested',
    reportDate: new Date().toLocaleDateString('en-US'),
    originalFileName: 'test-upload.csv',
    dataSet: 'test-dataset',
    dataset: 'test-dataset',
    recordsSubmitted: '1000',
    distinctRecognizedIdentities: '800',
    recordsWithRecognizedHashedIdentity: '750',
    recordsDroppedBeyondRetention: '50',
    totalAttributedUserRecords: '500',
    crmDataType: 'Sales',
    error: 'Sample error message for testing',
    errors: 'Sample error message for testing',
    attributionType: 'Sales',
    attributionDateMonth: 'January',
    attributionDateYear: '2025',
    startDateFormatted: '01/01/2025',
    endDateFormatted: '12/31/2025',
    advertiserCount: '15',
    offersExported: '42',
    totalCount: '100',
    totalImported: '85',
    totalSkipped: '10',
    totalErrors: '5',
    totalOffers: '50',
    totalNewOffers: '30',
    totalUpdatedOffers: '20',
    purecarsImportFileName: 'purecars-offers-2025.csv',
    hasError: 'false',
    dspCampaignId: '12345',
    gatewayAmazonDSPIsActive: '2',
};

// ---------------------------------------------------------------------------
// Group templates by category for the autocomplete dropdown
// ---------------------------------------------------------------------------
function getTemplateCategory(key: string): string {
    if (key.startsWith('CAMPAIGN')) return 'Campaign';
    if (key.startsWith('FIRST_PARTY') || key.startsWith('SALES_DATA')) return 'Sales / First Party Data';
    if (key.startsWith('AMC')) return 'AMC';
    if (key.includes('EXPORT') || key.includes('PERFORMANCE')) return 'Exports';
    if (key.startsWith('OEMOFFERS')) return 'OEM Offers';
    if (key === 'WELCOME_EMAIL' || key === 'NEW_ADVERTISER') return 'User / Advertiser';
    if (key === 'POLK_SALES_OPT_IN') return 'Polk';
    return 'Other';
}

// ---------------------------------------------------------------------------
// Pretty-print a camelCase variable name as a label
// ---------------------------------------------------------------------------
function variableToLabel(variable: string): string {
    return variable
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .trim();
}

// ---------------------------------------------------------------------------
// Resolve subject line placeholders like {advertiserName}
// ---------------------------------------------------------------------------
function resolveSubject(template: string, variables: Record<string, string>): string {
    return template.replace(/\{(\w+)\}/g, (_, key) => variables[key] || `{${key}}`);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function EmailSenderPage() {
    const [templates, setTemplates] = useState<EmailTemplateDTO[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateDTO | null>(null);
    const [recipientEmail, setRecipientEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [variables, setVariables] = useState<Record<string, string>>({});
    const [sending, setSending] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');
    const [loading, setLoading] = useState(true);
    const [infoMessage, setInfoMessage] = useState<InfoMessageProps>({ message: null });

    usePageTitle({ segments: ['Email Sender'] });

    // -----------------------------------------------------------------------
    // Fetch templates on mount
    // -----------------------------------------------------------------------
    useEffect(() => {
        let cancelled = false;
        ApiService.getEmailTemplates()
            .then((res) => {
                if (!cancelled) {
                    setTemplates(res.data);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    showMessage('error', 'Failed to load email templates');
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // -----------------------------------------------------------------------
    // Sort templates by category + displayName for the autocomplete
    // -----------------------------------------------------------------------
    const sortedTemplates = useMemo(() => {
        return [...templates].sort((a, b) => {
            const catA = getTemplateCategory(a.key);
            const catB = getTemplateCategory(b.key);
            if (catA !== catB) return catA.localeCompare(catB);
            return a.displayName.localeCompare(b.displayName);
        });
    }, [templates]);

    // -----------------------------------------------------------------------
    // When template selection changes, reset variables to defaults and update subject
    // -----------------------------------------------------------------------
    const handleTemplateChange = useCallback((_event: any, value: EmailTemplateDTO | null) => {
        setSelectedTemplate(value);
        if (value) {
            const defaults: Record<string, string> = {};
            value.variables.forEach((v) => {
                defaults[v] = VARIABLE_DEFAULTS[v] || variableToLabel(v);
            });
            setVariables(defaults);
            setSubject(resolveSubject(value.defaultSubject, defaults));
        } else {
            setVariables({});
            setSubject('');
        }
    }, []);

    // -----------------------------------------------------------------------
    // Keep subject line in sync when variables change
    // -----------------------------------------------------------------------
    const handleVariableChange = useCallback(
        (key: string, value: string) => {
            setVariables((prev) => {
                const next = { ...prev, [key]: value };
                if (selectedTemplate) {
                    setSubject(resolveSubject(selectedTemplate.defaultSubject, next));
                }
                return next;
            });
        },
        [selectedTemplate]
    );

    // -----------------------------------------------------------------------
    // Info message helper
    // -----------------------------------------------------------------------
    function showMessage(severity: AlertColor, message: string) {
        setInfoMessage({
            message,
            severity,
            onClose: () => setInfoMessage({ message: null }),
        });
    }

    // -----------------------------------------------------------------------
    // Send
    // -----------------------------------------------------------------------
    async function handleSend() {
        if (!selectedTemplate || !recipientEmail) return;
        setSending(true);
        try {
            const res = await ApiService.sendTestEmail({
                templateFile: selectedTemplate.templateFile,
                recipientEmail,
                subject,
                variables,
            });
            showMessage('success', res.data.message || 'Email sent successfully');
        } catch (err: any) {
            const msg = err?.response?.data?.message || 'Failed to send email';
            showMessage('error', msg);
        } finally {
            setSending(false);
        }
    }

    // -----------------------------------------------------------------------
    // Preview
    // -----------------------------------------------------------------------
    async function handlePreview() {
        if (!selectedTemplate) return;
        setPreviewing(true);
        try {
            const res = await ApiService.previewEmail({
                templateFile: selectedTemplate.templateFile,
                variables,
            });
            setPreviewHtml(res.data.html);
            setPreviewOpen(true);
        } catch (err: any) {
            const msg = err?.response?.data?.message || 'Failed to generate preview';
            showMessage('error', msg);
        } finally {
            setPreviewing(false);
        }
    }

    // -----------------------------------------------------------------------
    // Validation
    // -----------------------------------------------------------------------
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail);
    const canSend = !!selectedTemplate && isValidEmail && !sending;
    const canPreview = !!selectedTemplate && !previewing;

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
        <Column gap={0} flex={1}>
            <InfoMessage {...infoMessage} />

            <Block>
                <Column gap={1}>
                    <Heading>Email Sender</Heading>
                    <Typography variant="body2" color="text.secondary">
                        Test and preview email templates. Emails sent from this page will be prefixed with [TEST].
                    </Typography>
                </Column>
            </Block>

            <Block>
                <Grid container spacing={3}>
                    {/* Row 1: Recipient + Template */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            label="Recipient Email"
                            type="email"
                            fullWidth
                            size="small"
                            value={recipientEmail}
                            onChange={(e) => setRecipientEmail(e.target.value)}
                            error={recipientEmail.length > 0 && !isValidEmail}
                            helperText={
                                recipientEmail.length > 0 && !isValidEmail ? 'Enter a valid email address' : ' '
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Autocomplete
                            options={sortedTemplates}
                            groupBy={(option) => getTemplateCategory(option.key)}
                            getOptionLabel={(option) => option.displayName}
                            value={selectedTemplate}
                            onChange={handleTemplateChange}
                            loading={loading}
                            size="small"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Email Template"
                                    helperText=" "
                                    slotProps={{
                                        input: {
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    {/* Row 2: Subject */}
                    {selectedTemplate && (
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="Subject Line"
                                fullWidth
                                size="small"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                helperText="Will be prefixed with [TEST] when sent"
                            />
                        </Grid>
                    )}

                    {/* Row 3: Dynamic variable fields */}
                    {selectedTemplate &&
                        selectedTemplate.variables.map((variable) => (
                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={variable}>
                                <TextField
                                    label={variableToLabel(variable)}
                                    fullWidth
                                    size="small"
                                    value={variables[variable] || ''}
                                    onChange={(e) => handleVariableChange(variable, e.target.value)}
                                />
                            </Grid>
                        ))}

                    {/* Row 4: Actions */}
                    {selectedTemplate && (
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={
                                        previewing ? <CircularProgress size={18} color="inherit" /> : <Visibility />
                                    }
                                    onClick={handlePreview}
                                    disabled={!canPreview}
                                >
                                    Preview
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <Send />}
                                    onClick={handleSend}
                                    disabled={!canSend}
                                >
                                    Send Test Email
                                </Button>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Block>

            {/* Preview dialog */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Email Preview: {selectedTemplate?.displayName}</DialogTitle>
                <DialogContent dividers>
                    <iframe
                        srcDoc={previewHtml}
                        title="Email Preview"
                        style={{
                            width: '100%',
                            height: '600px',
                            border: 'none',
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Column>
    );
}
